import axios from "axios";

const RECAPTCHA_SITE_KEY = "6LdmsQIrAAAAAFk90vfXAM38LpbvyJYsDO8eInG0";

/**
 * Get a reCAPTCHA v3 token for the given action.
 * Waits for the grecaptcha script to be ready before executing.
 */
function getRecaptchaToken(action) {
  return new Promise((resolve) => {
    if (!window.grecaptcha) {
      console.warn("reCAPTCHA not loaded");
      resolve("");
      return;
    }
    window.grecaptcha.ready(async () => {
      try {
        const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
          action,
        });
        console.log("reCAPTCHA token obtained, length:", token?.length);
        resolve(token);
      } catch (e) {
        console.warn("reCAPTCHA execute failed", e);
        resolve("");
      }
    });
  });
}

/**
 * Login via LoginJsonController (POST /lmiga/user/login.json).
 * The server sets an alertsLoggedIn cookie on success for session auth.
 *
 * @param {{ email: string, password: string }} credentials
 * @returns {{ email: string }}
 */
export const login = async ({ email, password }) => {
  try {
    const response = await axios.post("/lmiga/user/login.json", {
      username: email,
      password,
    });
    const data = response.data;

    if (!data.success) {
      throw new Error(data.error?.message || "Login failed");
    }

    return { email, ...(data.data || {}) };
  } catch (error) {
    if (error.response?.data?.error?.message) {
      throw new Error(error.response.data.error.message);
    }
    throw new Error(error.message || "Login failed");
  }
};

/**
 * Register via AlertsUserRegisterJsonController (POST /lmiga/user/trip-register.json).
 * Note: registration does NOT auto-login. The user must verify their email first, then log in.
 *
 * New accounts default to HTML email format and no vacation dates; both are
 * editable on the My Account page (gateway#11).
 *
 * @param {{ email: string, password: string, recaptchaResponse?: string }} data
 * @returns {{ email: string, message: string }}
 */
export const register = async ({ email, password }) => {
  try {
    // Obtain a reCAPTCHA v3 token (script loaded in index.html)
    const recaptchaToken = await getRecaptchaToken("trip_register");

    const response = await axios.post("/lmiga/user/trip-register.json", {
      email,
      password,
      format: "HTML",
      recaptchaResponse: recaptchaToken,
    });

    const data = response.data;

    if (!data.success) {
      throw buildRegistrationError(data);
    }

    return {
      email,
      message:
        data.data?.message ||
        "Registration successful. Check your email to verify your account.",
    };
  } catch (error) {
    // If we already built a structured error, re-throw it
    if (error.fieldErrors) {
      throw error;
    }
    if (error.response?.data) {
      throw buildRegistrationError(error.response.data);
    }
    throw new Error(error.message || "Registration failed");
  }
};

/**
 * Request a password reset email.
 *
 * @param {string} email
 * @returns {Promise<void>}
 */
export const requestPasswordReset = async (email) => {
  try {
    const recaptchaToken = await getRecaptchaToken("trip_password_reset");
    const response = await axios.post(
      "/lmiga/user/trip-password-reset-request.json",
      { email, recaptchaResponse: recaptchaToken },
    );
    const data = response.data;
    if (data.success === false) {
      throw new Error(data.error?.message || data.message || "Request failed");
    }
  } catch (error) {
    if (error.response?.data) {
      const d = error.response.data;
      throw new Error(d.error?.message || d.message || "Request failed");
    }
    throw error;
  }
};

/**
 * Reset password using a token from the password reset email.
 *
 * @param {string} token  the reset token from the URL
 * @param {string} newPassword
 * @returns {Promise<void>}
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await axios.post("/lmiga/user/trip-password-reset.json", {
      token,
      newPassword: newPassword,
    });
    const data = response.data;
    if (data.success === false) {
      throw new Error(
        data.error?.message || data.message || "Password reset failed",
      );
    }
  } catch (error) {
    if (error.response?.data) {
      const d = error.response.data;
      throw new Error(d.error?.message || d.message || "Password reset failed");
    }
    throw error;
  }
};

/**
 * Verify an email address using the code from the verification email.
 *
 * @param {string} code
 * @returns {Promise<void>}
 */
export const verifyEmail = async (code) => {
  try {
    const response = await axios.get("/lmiga/user/trip-verify.json", {
      params: { code },
    });
    const data = response.data;
    if (data.success === false) {
      throw new Error(
        data.error?.message || data.message || "Verification failed",
      );
    }
  } catch (error) {
    if (error.response?.data) {
      const d = error.response.data;
      throw new Error(d.error?.message || d.message || "Verification failed");
    }
    throw error;
  }
};

/**
 * Build a structured error from the server's ApiResponse error envelope.
 * Server format: { success: false, error: { code, message, fieldErrors: { field: msg } } }
 */
function buildRegistrationError(respData) {
  const fieldErrors = respData.error?.fieldErrors || respData.fieldErrors || {};
  const message =
    respData.error?.message || respData.message || "Registration failed";

  const err = new Error(message);
  err.fieldErrors = fieldErrors;
  return err;
}
